import React, { memo } from "react";
import { Typography as MuiTypography } from "@mui/material";
import { blackColor } from "../../config/ColorObj";

/**
 * TypographyCompo - Reusable Typography component (React.js version)
 *
 * Features:
 * - Supports both `title` prop and `children`
 * - React.memo for performance optimization
 * - Sensible defaults (variant='body2', textTransform='none')
 * - Supports all MUI Typography props
 * - Custom color support via colors config
 * - Allows fontSize, fontWeight, and custom styling via sx
 *
 * @example
 * <TypographyCompo title="Hello World" variant="h6" />
 *
 * <TypographyCompo variant="body1">Hello World</TypographyCompo>
 *
 * <TypographyCompo
 *   title="Custom Text"
 *   fontSize="14px"
 *   fontWeight={600}
 *   color={colors.primary}
 *   sx={{ mb: 2 }}
 * />
 */
const TypographyCompo = memo(({
    title = "",
    variant = "body2",
    fontSize,
    fontWeight,
    alignSelf,
    color,
    sx = {},
    onClick,
    children,
    ...otherProps
}) => {
    // Prefer children over title
    const content = children !== undefined ? children : title;

    // Default color fallback
    const textColor = color || blackColor;

    // Base styles
    const baseStyles = {
        textTransform: "none",
        color: textColor,
        ...(fontSize && { fontSize }),
        ...(fontWeight && { fontWeight }),
        ...(alignSelf && { alignSelf }),
    };

    // Merge styles safely (MUI supports array sx)
    const mergedSx = sx ? [baseStyles, sx] : baseStyles;

    return (
        <MuiTypography
            variant={variant}
            sx={mergedSx}
            onClick={onClick}
            {...otherProps}
        >
            {content}
        </MuiTypography>
    );
});

TypographyCompo.displayName = "TypographyCompo";

export default TypographyCompo;
